import { useEffect, useState, useRef } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { fetchOrder } from "../lib/api";
import { CheckCircle2, Clock, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 2000;
const POLL_MAX = 30; // 30 × 2s = 60s total

const Confirmation = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  // payment state: null (no stripe in flow) | 'polling' | 'paid' | 'unpaid' | 'expired' | 'error'
  const [payment, setPayment] = useState(sessionId ? "polling" : null);
  const [paymentMsg, setPaymentMsg] = useState("");
  const attempts = useRef(0);

  // Reload the order
  const reloadOrder = () => fetchOrder(id).then(setOrder).catch(() => {});
  useEffect(() => { reloadOrder(); /* eslint-disable-next-line */ }, [id]);

  // Poll Stripe session status when we came back from Checkout
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!cancelled) { setPayment("paid"); setPaymentMsg("Your payment has been received."); reloadOrder(); }
          return;
        }
        if (data.status === "expired") {
          if (!cancelled) { setPayment("expired"); setPaymentMsg("This checkout session expired. Please try again."); }
          return;
        }
        if (attempts.current >= POLL_MAX) {
          if (!cancelled) { setPayment("unpaid"); setPaymentMsg("We couldn't confirm payment in time. You can refresh once your card has processed."); }
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        if (!cancelled) { setPayment("error"); setPaymentMsg("Could not check payment status."); }
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]); // eslint-disable-line

  if (!order) return <div className="vd-container py-20"><p className="vd-muted">Loading confirmation...</p></div>;

  const showPaidBanner = payment === "paid" || order.status === "paid";
  const showWaiting = payment === "polling";
  const showFailure = payment === "expired" || payment === "unpaid" || payment === "error";

  return (
    <div className="vd-page" data-testid="page-confirmation">
      <section className="vd-container py-16 max-w-2xl">
        {showWaiting && (
          <div className="vd-card p-5 mb-6 flex items-center gap-3" style={{ borderColor: "var(--primary-red)" }} data-testid="conf-polling">
            <Clock size={22} color="var(--primary-red)" />
            <div>
              <p className="vd-heading font-semibold">Verifying your payment…</p>
              <p className="vd-muted text-sm">This usually takes a couple of seconds. Please don't close this tab.</p>
            </div>
          </div>
        )}
        {showFailure && (
          <div className="vd-card p-5 mb-6 flex items-center gap-3" style={{ borderColor: "var(--primary-red)" }} data-testid="conf-payment-issue">
            <XCircle size={22} color="var(--primary-red)" />
            <div className="flex-1">
              <p className="vd-heading font-semibold">Payment not confirmed</p>
              <p className="vd-muted text-sm">{paymentMsg}</p>
            </div>
            <Link to="/checkout" className="vd-btn-primary text-sm" data-testid="conf-retry-payment">Retry</Link>
          </div>
        )}

        <div className="text-center mb-10">
          <div className="mx-auto mb-5 flex items-center justify-center rounded-full" style={{ width: 72, height: 72, background: "var(--primary-red)" }}>
            <CheckCircle2 size={36} color="#fff" />
          </div>
          <h1 className="mb-2">{showPaidBanner ? "Payment Received" : "Order Confirmed"}</h1>
          <p className="vd-muted">Thank you, {order.full_name}! {showPaidBanner ? "Your order is now paid and being scheduled." : "We've sent a confirmation to your phone."}</p>
        </div>

        <div className="vd-card p-6" data-testid="confirmation-card">
          <div className="flex items-center justify-between mb-1">
            <p className="vd-muted text-sm">Order Reference</p>
            <span
              className="text-xs font-bold rounded-full px-3 py-0.5"
              style={{
                background: showPaidBanner ? "rgba(0,180,80,0.15)" : "rgba(255,179,11,0.18)",
                color: showPaidBanner ? "#00A35C" : "#B57E00",
              }}
              data-testid="conf-status-badge"
            >{(order.status || "confirmed").toUpperCase()}</span>
          </div>
          <p className="vd-heading font-bold mb-5" data-testid="order-id">{order.id}</p>

          <h3 className="mb-3">Items</h3>
          <div className="space-y-3 mb-5">
            {order.items.map((i, idx) => (
              <div key={idx} className="flex items-center gap-3 pb-3 border-b" style={{ borderColor: "var(--border-color)" }}>
                {i.image && <img src={i.image} alt={i.name} className="w-12 h-12 rounded-md object-cover" />}
                <div className="flex-1"><p className="vd-heading text-sm font-semibold">{i.name}</p><p className="vd-muted text-xs">Qty: {i.quantity}</p></div>
                <p className="font-bold" style={{ color: "var(--primary-red)" }}>£{(i.price * i.quantity).toFixed(2)}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm mb-5">
            <Field k="Phone" v={order.phone} />
            <Field k="Postcode" v={order.postcode} />
            <Field k="Address" v={order.address} />
            <Field k="Fulfilment" v={(order.service_type || "mobile_fitting") === "delivery" ? "Delivery only (no fitting)" : "Mobile fitting"} />
            {(order.service_type || "mobile_fitting") !== "delivery" && (
              <>
                <Field k="Fitting Date" v={order.fitting_date} />
                <Field k="Time Slot" v={order.time_slot} />
              </>
            )}
            <Field k="Payment" v={order.payment_method.toUpperCase()} />
          </div>

          <div className="flex justify-between border-t pt-4" style={{ borderColor: "var(--border-color)" }}>
            <span className="vd-heading font-bold">{showPaidBanner ? "Total Paid" : "Order Total"}</span>
            <span className="font-extrabold" style={{ color: "var(--primary-red)" }}>£{order.total.toFixed(2)}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 justify-center mt-8">
          <Link to="/" className="vd-btn-primary" data-testid="conf-home">Back to Home</Link>
          <Link to={`/order/track?id=${order.id}&postcode=${encodeURIComponent(order.postcode)}`} className="vd-btn-secondary" data-testid="conf-track">Track this order</Link>
          <Link to="/tyres" className="vd-btn-secondary" data-testid="conf-shop">Continue Shopping</Link>
        </div>
      </section>
    </div>
  );
};

const Field = ({ k, v }) => (
  <div><p className="vd-muted text-xs uppercase tracking-wider">{k}</p><p className="vd-heading font-medium">{v}</p></div>
);

export default Confirmation;
